import { useState } from "react";
import { X, ChevronRight, Layers } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { fmtTime, programColor, isFull, isBookingClosed, toMinutes, fmtShort, weekdayOf } from "../../lib/calendarUtils";

const HOUR_PX = 60;
const fmtMoney = (n) => (typeof n === "number" ? `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}` : "");

function slotBadge(s) {
  if (isBookingClosed(s)) return { label: "Booking closed", cls: "bg-slate-200 text-slate-600" };
  if (isFull(s)) return { label: "Full", cls: "bg-slate-300 text-slate-700" };
  return { label: `${s.seats_available} seat${s.seats_available === 1 ? "" : "s"}`, cls: "bg-white/80 text-slate-700" };
}

function priceShort(s) {
  const opts = s.payment_options || [];
  const p = (opts[0] && opts[0].pricing) || s.pricing || {};
  const rec = opts[0] ? opts[0].recurring : s.billing_type === "subscription";
  return rec ? `${fmtMoney(p.monthly_amount)}/mo` : fmtMoney(p.total_due_today);
}

/** Group same-day occurrences by identical start_time; assign collision lanes to
 *  the resulting items (groups + singles) so partial overlaps never hide a class. */
function layoutDay(occs) {
  const byStart = {};
  for (const o of occs) (byStart[o.schedule.start_time || "?"] ||= []).push(o);
  const items = Object.entries(byStart).map(([startTime, group]) => {
    const start = toMinutes(startTime) ?? 9 * 60;
    const end = Math.max(...group.map((o) => toMinutes(o.schedule.end_time) ?? start + 60));
    return { startTime, start, end, group };
  });
  items.sort((a, b) => a.start - b.start || a.end - b.end);
  // overlap clusters → lanes
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const laneEnds = [];
    for (const it of cluster) {
      let lane = laneEnds.findIndex((e) => e <= it.start);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); } else laneEnds[lane] = it.end;
      it.lane = lane;
    }
    for (const it of cluster) it.laneCount = laneEnds.length;
    cluster = [];
  };
  for (const it of items) {
    if (cluster.length && it.start >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd === -1 ? 0 : clusterEnd, it.end);
  }
  if (cluster.length) flush();
  return items;
}

function GroupRows({ group, selectedKey, onSelect }) {
  return (
    <div className="max-h-72 overflow-y-auto overscroll-contain grid gap-1 pr-0.5" data-testid="group-class-list">
      {group.map((o) => {
        const s = o.schedule;
        const c = programColor(s);
        const sel = o.key === selectedKey;
        const badge = slotBadge(s);
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onSelect(o)}
            data-testid={`group-class-row-${o.key}`}
            aria-pressed={sel}
            className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A6B2] ${
              sel ? "border-[#123B6D] bg-[#EAF7FB]" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            <span className="h-8 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: c.hex }} />
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-extrabold text-slate-900 truncate">{s.title}</span>
              <span className="block text-[10px] text-slate-500 truncate">
                {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
                {s.age_range ? ` · Ages ${s.age_range}` : ""} · {priceShort(s)}
              </span>
            </span>
            <span className={`shrink-0 rounded-full px-1.5 py-px text-[9px] font-bold ${badge.cls}`}>{badge.label}</span>
            <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${sel ? "text-[#123B6D]" : "text-slate-300"}`} />
          </button>
        );
      })}
    </div>
  );
}

export default function WeekCalendar({ dates, occurrences, selectedKey, onSelect, todayStr }) {
  const [openGroup, setOpenGroup] = useState(null); // `${date}_${startTime}`

  const mins = occurrences.map((o) => toMinutes(o.schedule.start_time) ?? 540);
  const maxs = occurrences.map((o) => toMinutes(o.schedule.end_time) ?? (toMinutes(o.schedule.start_time) ?? 540) + 60);
  const gridStart = Math.min(9 * 60, ...(mins.length ? mins : [9 * 60]));
  const gridEnd = Math.max(18 * 60, ...(maxs.length ? maxs : [18 * 60]));
  const startH = Math.floor(gridStart / 60);
  const endH = Math.ceil(gridEnd / 60);
  const hours = Array.from({ length: endH - startH }, (_, i) => startH + i);
  const gridHeight = hours.length * HOUR_PX;

  const byDate = {};
  for (const o of occurrences) (byDate[o.date] ||= []).push(o);

  const laneStyle = (it) => {
    const n = it.laneCount || 1;
    const w = 100 / n;
    return { left: `calc(${it.lane * w}% + 2px)`, width: `calc(${w}% - 4px)` };
  };

  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white overflow-hidden" data-testid="week-calendar">
      <div className="grid border-b border-slate-200" style={{ gridTemplateColumns: "56px repeat(7, minmax(0,1fr))" }}>
        <div />
        {dates.map((d) => {
          const isToday = d === todayStr;
          return (
            <div key={d} className={`py-2.5 text-center border-l border-slate-100 ${isToday ? "bg-[#EAF7FB]" : ""}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{weekdayOf(d)}</p>
              <p className={`text-sm font-extrabold font-display ${isToday ? "text-[#00A6B2]" : "text-slate-700"}`}
                data-testid={`calendar-day-header-${weekdayOf(d).toLowerCase()}`}>
                {fmtShort(d)}
              </p>
            </div>
          );
        })}
      </div>

      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, minmax(0,1fr))" }}>
        <div className="relative" style={{ height: gridHeight }}>
          {hours.map((h, i) => (
            <span key={h} className="absolute right-1.5 -translate-y-1/2 text-[10px] font-semibold text-slate-400" style={{ top: i * HOUR_PX }}>
              {i === 0 ? "" : fmtTime(`${h}:00`).replace(":00", "")}
            </span>
          ))}
        </div>
        {dates.map((d) => {
          const items = layoutDay(byDate[d] || []);
          return (
            <div key={d} className={`relative border-l border-slate-100 ${d === todayStr ? "bg-[#EAF7FB]/40" : ""}`} style={{ height: gridHeight }}>
              {hours.map((h, i) => (
                <div key={h} className="absolute inset-x-0 border-t border-slate-100" style={{ top: i * HOUR_PX }} />
              ))}
              {items.map((it) => {
                const top = ((it.start - startH * 60) / 60) * HOUR_PX;
                const height = Math.max(((it.end - it.start) / 60) * HOUR_PX, 52);
                const groupId = `${d}_${it.startTime}`;

                if (it.group.length === 1) {
                  const o = it.group[0];
                  const s = o.schedule;
                  const c = programColor(s);
                  const sel = o.key === selectedKey;
                  const badge = slotBadge(s);
                  const narrow = (it.laneCount || 1) > 1;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => onSelect(o)}
                      data-testid={`calendar-slot-${o.key}`}
                      aria-pressed={sel}
                      className={`absolute rounded-xl border-l-4 ${c.bg} ${c.border} ${c.text} text-left px-2 py-1.5 overflow-hidden transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A6B2] ${
                        sel ? "ring-2 ring-[#123B6D] shadow-md z-10" : "hover:shadow-sm"
                      } ${isFull(s) || isBookingClosed(s) ? "opacity-75" : ""}`}
                      style={{ top, height, ...laneStyle(it) }}
                    >
                      <p className="text-[11px] font-extrabold leading-tight truncate">{s.title}</p>
                      <p className="text-[10px] font-semibold opacity-80 truncate">
                        {fmtTime(s.start_time)}{!narrow && `–${fmtTime(s.end_time)}`}
                      </p>
                      {height >= 70 && !narrow && (
                        <p className="text-[10px] opacity-80 truncate">
                          {s.age_range ? `Ages ${s.age_range}` : ""} · {priceShort(s)}
                        </p>
                      )}
                      {height >= 88 && (
                        <span className={`mt-0.5 inline-block rounded-full px-1.5 py-px text-[9px] font-bold ${badge.cls}`}>{badge.label}</span>
                      )}
                    </button>
                  );
                }

                // Grouped indicator: 2+ classes sharing the same start time
                const containsSelected = it.group.some((o) => o.key === selectedKey);
                return (
                  <Popover key={groupId} open={openGroup === groupId} onOpenChange={(o) => setOpenGroup(o ? groupId : null)}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid={`calendar-group-${groupId}`}
                        aria-label={`${it.group.length} classes at ${fmtTime(it.startTime)} — open list`}
                        className={`absolute rounded-xl border-2 border-dashed text-left px-2 py-1.5 overflow-hidden transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A6B2] hover:shadow-md ${
                          containsSelected || openGroup === groupId
                            ? "border-[#123B6D] bg-[#EAF7FB] ring-2 ring-[#123B6D]/30 z-10"
                            : "border-[#00A6B2]/60 bg-[#E6F7F8] hover:border-[#00A6B2]"
                        }`}
                        style={{ top, height, ...laneStyle(it) }}
                      >
                        <p className="inline-flex items-center gap-1 text-[11px] font-extrabold leading-tight text-[#123B6D]">
                          <Layers className="h-3 w-3 shrink-0" /> {it.group.length} classes
                        </p>
                        <p className="text-[10px] font-semibold text-slate-600 truncate">{fmtTime(it.startTime)}</p>
                        {height >= 70 && <p className="text-[10px] font-bold text-[#00A6B2]">Tap to choose ▾</p>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="right"
                      align="start"
                      collisionPadding={12}
                      className="w-80 p-3 rounded-2xl shadow-xl border-slate-200"
                      data-testid={`group-popover-${groupId}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-extrabold font-display" style={{ color: "#123B6D" }}>
                          {it.group.length} classes at {fmtTime(it.startTime)}
                        </p>
                        <button
                          type="button"
                          onClick={() => setOpenGroup(null)}
                          aria-label="Close class list"
                          data-testid="group-popover-close"
                          className="rounded-full p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <GroupRows group={it.group} selectedKey={selectedKey} onSelect={onSelect} />
                    </PopoverContent>
                  </Popover>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
